import type { FC } from "react"
import { memo, useEffect, useMemo, useRef, useState } from "react"

import type { TimelineEvent } from "@productlane/api/src/browser"
import type { LiveChatUserTypingData } from "@productlane/lib"
import type { FormattedAttachment } from "@productlane/ui"
import { EmailContactSchema } from "@productlane/api/src/browser"
import { generateNameFromEmail } from "@productlane/api/src/lib/name-from-email"
import { cn } from "@productlane/lib/src/cn"
import {
	Avatar,
	Loader,
	ScrollArea,
	ScrollViewport,
	TypingProgress,
	useTypingUsers,
} from "@productlane/ui"
import { feedbackMessagePreloadSize } from "@productlane/zero/queries"

import type { TimelineGating } from "../../lib/timeline-pagination"
import type { Editor } from "@tiptap/react"
import {
	useHighlights,
	useMarkHighlights,
} from "@/components/editor/editor-config"
import { getContactDisplayName } from "@/lib/email-utils"
import { useLiveChatStore } from "@/lib/states/live-chat-state"
import { messageIds } from "@/lib/unsent-messages"
import { useDocTyping, useMemberEmails } from "@/lib/use-doc-presence"
import { useTimelineEvents } from "../../hooks/useTimelineEvents"
import { useTimelinePages } from "../../hooks/useTimelinePages"
import { computeTimelineGating } from "../../lib/timeline-pagination"
import { isThreadNoteEditable, useThreadContext } from "../../thread-context"
import { THREAD_COLUMN_PADDING } from "./thread-column"
import { ActivityEvent } from "./timeline-events/activity-event"
import { MIN_FOLDED_RUN_LENGTH } from "./timeline-events/activity-run-fold"
import { AgentActivityBurstEvent } from "./timeline-events/agent-activity-burst-event"
import { getAiChatFeedbackComment } from "./timeline-events/ai-chat-feedback-comment"
import { AiChatFeedbackEvent } from "./timeline-events/ai-chat-feedback-event"
import {
	isTypingForSession,
	TIMELINE_BOTTOM_FADE_CLASS,
	TIMELINE_BOTTOM_PADDING_CLASS,
	TYPING_ANCHOR_CLASS,
	TYPING_AVATAR_COLUMN_CLASS,
	TYPING_LABEL_GAP_CLASS,
	TYPING_ROW_CLASS,
} from "./timeline-events/base/typing-row"
import { ChatMessageEvent } from "./timeline-events/chat-message-event"
import { CollapsibleActivityRun } from "./timeline-events/collapsible-activity-run"
import { CommentEvent } from "./timeline-events/comment-event"
import {
	getEventStableId,
	isDeviceInfoCommentEvent,
	resolveDeviceInfoPlacement,
} from "./timeline-events/device-info"
import { DeviceInfoEvent } from "./timeline-events/device-info-event"
import { EmailEvent } from "./timeline-events/email-event"
import { isFeatureRequestCardEvent } from "./timeline-events/feature-request-card"
import { FeatureRequestCardEvent } from "./timeline-events/feature-request-card-event"
import { LiveChatClosedEvent } from "./timeline-events/live-chat-closed-event"
import { LiveChatMessageEvent } from "./timeline-events/live-chat-message-event"
import { LiveChatSystemEvent } from "./timeline-events/live-chat-system-event"
import { isResolvedCommentEvent } from "./timeline-events/resolved-comment-runs"
import { ResolvedCommentsRun } from "./timeline-events/resolved-comments-run"
import { RoutineNoteEvent } from "./timeline-events/routine-note-event"
import { RoutineRunEvent } from "./timeline-events/routine-run-event"
import {
	isHiddenShadowReply,
	isShadowReplyEvent,
	latestAnswerAt,
} from "./timeline-events/shadow-reply"
import { ShadowReplyEvent } from "./timeline-events/shadow-reply-event"
import { joinedSlackMessages } from "./timeline-events/slack-grouping"
import {
	SlackGroupingContext,
	SlackGroupingNotice,
} from "./timeline-events/slack-grouping-notice"
import { SlackMessageEvent } from "./timeline-events/slack-message-event"
import { systemEventEdgeSpacing } from "./timeline-events/system-event-spacing"
import { ThreadContentEvent } from "./timeline-events/thread-content-event"
import { ThreadCreatedEvent } from "./timeline-events/thread-created-event"
import { ThreadMessageEvent } from "./timeline-events/thread-message-event"
import { MessageEnter, UnsentMessages } from "./timeline-events/unsent-messages"
import { scrollTopAfterResize } from "./timeline-scroll"
import {
	shouldShowTimelineSkeleton,
	TimelineSkeleton,
} from "./timeline-skeleton"

interface Props {
	editor: Editor | null
	onMessageUndo: (content: string) => void
	isEditorOpen: boolean
	onHideNotes?: () => void
}

// Helper function to extract sender info from an event
const getEventSenderInfo = (event: TimelineEvent) => {
	switch (event.type) {
		case "email":
			return {
				senderId:
					event.email.fromContactId ||
					EmailContactSchema.parse(event.email.from).email,
				eventType: "email",
			}
		case "slackMessage":
			return {
				senderId:
					event.slackMessage.fromContactId || event.slackMessage.slackUserId,
				eventType: "slackMessage",
			}
		case "chatMessage":
			return {
				senderId:
					event.chatMessage.userId ||
					event.chatMessage.authorId ||
					event.chatMessage.authorName,
				eventType: "chatMessage",
			}
		case "comment":
			// Device-info comments (no userId, no Linear origin) are non-groupable
			// system events. Linear comments render as real comments.
			if (!event.comment.userId && !event.comment.linearCommentId) {
				return { senderId: null, eventType: "other" }
			}
			// Each comment thread is its own box (parent + replies + composer), so
			// never merge consecutive comments into one visual group.
			return {
				senderId: event.comment.id,
				eventType: "comment",
			}
		case "threadMessage":
			return {
				senderId: event.threadMessage.actorName,
				eventType: "threadMessage",
			}
		case "live-chat-message":
			// SYSTEM rows are timeline events, not messages, so they join the
			// system run instead of rendering as a Productlane Agent bubble.
			if (event.liveChatMessage.role === "SYSTEM") {
				return { senderId: null, eventType: "other" }
			}
			return {
				senderId:
					event.liveChatMessage.email ||
					event.liveChatMessage.name ||
					event.liveChatMessage.role,
				eventType: "live-chat-message",
			}
		default:
			return {
				senderId: null,
				eventType: "other",
			}
	}
}

// Every message renders as its own group (and box), with its avatar.
const groupEventsBySender = (events: TimelineEvent[]) => {
	return events.map((event) => {
		const { senderId, eventType } = getEventSenderInfo(event)
		return {
			events: [event],
			senderId: eventType === "other" ? null : (senderId ?? null),
			type: eventType,
		}
	})
}

const isAiChatFeedbackEvent = (event: TimelineEvent): boolean =>
	event.type === "activity" &&
	event.threadActivity.eventType === "ai_chat_feedback"

// Helper function to check if an event is a system event (uses SystemEvent component)
const isSystemEvent = (event: TimelineEvent): boolean => {
	return (
		(event.type === "activity" &&
			!isShadowReplyEvent(event) &&
			!isFeatureRequestCardEvent(event)) ||
		event.type === "agentActivityBurst" ||
		event.type === "routineRun" ||
		(event.type === "comment" &&
			!event.comment.userId &&
			!event.comment.linearCommentId) ||
		(event.type === "live-chat-message" &&
			event.liveChatMessage.role === "SYSTEM") ||
		(event.type === "createdAt" && event.thread.origin === "INAPP")
	)
}

// Helper function to render non-groupable events
const renderNonGroupableEvent = (
	event: TimelineEvent,
	hasEventAbove: boolean,
	hasEventBelow: boolean,
) => {
	switch (event.type) {
		case "createdAt":
			return (
				<ThreadCreatedEvent
					key={`created-${event.thread.id}`}
					event={event}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow}
				/>
			)
		case "live-chat-closed":
			return (
				<LiveChatClosedEvent key={`live-chat-closed-${event.liveChat.id}`} />
			)
		case "live-chat-message":
			return (
				<LiveChatSystemEvent
					key={`live-chat-${event.liveChatMessage.id}`}
					event={event}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow}
				/>
			)
		case "comment":
			if (getAiChatFeedbackComment(event)) {
				return (
					<AiChatFeedbackEvent
						key={`ai-chat-feedback-${event.comment.id}`}
						event={event}
						hasEventAbove={hasEventAbove}
						hasEventBelow={hasEventBelow}
					/>
				)
			}
			// Device-info comments rendered as system events
			return (
				<DeviceInfoEvent
					key={`device-info-${event.comment.id}`}
					event={event}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow}
				/>
			)
		case "activity":
			if (isShadowReplyEvent(event)) {
				return <ShadowReplyEvent key={event.threadActivity.id} event={event} />
			}
			if (isFeatureRequestCardEvent(event)) {
				return (
					<FeatureRequestCardEvent
						key={event.threadActivity.id}
						event={event}
					/>
				)
			}
			if (event.threadActivity.eventType === "routine_note") {
				return (
					<RoutineNoteEvent
						key={event.threadActivity.id}
						event={event}
						hasEventAbove={hasEventAbove}
						hasEventBelow={hasEventBelow}
					/>
				)
			}
			return (
				<ActivityEvent
					key={event.threadActivity.id}
					event={event}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow}
				/>
			)
		case "routineRun":
			return (
				<RoutineRunEvent
					key={event.run.id}
					event={event}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow}
				/>
			)
		case "agentActivityBurst":
			return (
				<AgentActivityBurstEvent
					key={event.burst.id}
					event={event}
					hasEventAbove={hasEventAbove}
					hasEventBelow={hasEventBelow}
				/>
			)
		default:
			return null
	}
}

// Helper function to render grouped message events
const renderGroupedMessages = (
	group: {
		events: TimelineEvent[]
		senderId: string | null
		type: string
	},
	onMessageUndo: (content: string) => void,
	firstEmailId: string | undefined,
	deviceInfoForEventId: string | undefined,
	deviceInfoText: string | undefined,
) => {
	return (
		<div className="my-2 flex flex-col gap-1">
			{group.events.map((event: TimelineEvent, eventIndex: number) => {
				const isFirstInGroup = eventIndex === 0
				const eventDeviceInfo =
					deviceInfoForEventId &&
					getEventStableId(event) === deviceInfoForEventId
						? deviceInfoText
						: undefined

				switch (event.type) {
					case "email":
						return (
							<MessageEnter key={`email-${event.email.id}`} id={event.email.id}>
								<EmailEvent
									event={event}
									onUndo={onMessageUndo}
									showAvatar={true}
									isFirstMessage={event.email.id === firstEmailId}
									deviceInfoText={eventDeviceInfo}
								/>
							</MessageEnter>
						)
					case "slackMessage":
						return (
							<MessageEnter
								key={`slack-${event.slackMessage.id}`}
								id={event.slackMessage.id}
							>
								<SlackGroupingNotice beforeMessageId={event.slackMessage.id} />
								<SlackMessageEvent
									event={event}
									onUndo={onMessageUndo}
									showAvatar={isFirstInGroup}
									deviceInfoText={eventDeviceInfo}
								/>
							</MessageEnter>
						)
					case "chatMessage":
						return (
							<ChatMessageEvent
								key={`chat-${event.chatMessage.id}`}
								event={event}
								showAvatar={isFirstInGroup}
								deviceInfoText={eventDeviceInfo}
							/>
						)
					case "comment":
						return (
							<CommentEvent
								key={`comment-${event.comment.id}`}
								event={event}
								showAvatar={isFirstInGroup}
							/>
						)
					case "threadMessage":
						return (
							<ThreadMessageEvent
								key={`thread-${event.threadMessage.id}`}
								event={event}
								showAvatar={isFirstInGroup}
							/>
						)

					case "live-chat-message":
						return (
							<LiveChatMessageEvent
								key={`live-chat-${event.liveChatMessage.id}`}
								message={event.liveChatMessage}
								contactIsUnverified={event.contactIsUnverified}
								showAvatar={isFirstInGroup}
								deviceInfoText={eventDeviceInfo}
							/>
						)
					default:
						return null
				}
			})}
		</div>
	)
}

// Collect consecutive system event runs from grouped events
type RenderItem =
	| {
			type: "run"
			events: TimelineEvent[]
			hasRunAbove: boolean
			hasRunBelow: boolean
	  }
	| {
			type: "group"
			group: { events: TimelineEvent[]; senderId: string | null; type: string }
			// Keyed by the group's own event: a key from its position remounts every
			// message (and closes its open panels) whenever an event lands below it.
			key: string
	  }
	| {
			type: "single-other"
			event: TimelineEvent
			hasEventAbove: boolean
			hasEventBelow: boolean
	  }
	| {
			type: "resolved-comments"
			events: TimelineEvent[]
	  }

const groupKey = (events: TimelineEvent[]) => {
	const newest = events[events.length - 1]
	return newest ? `group-${getEventStableId(newest)}` : "group-empty"
}

export const buildRenderItems = (
	groupedEvents: ReturnType<typeof groupEventsBySender>,
): RenderItem[] => {
	const items: RenderItem[] = []
	let currentRun: TimelineEvent[] = []
	let runStartIndex = 0

	const pushSingleSystemEvent = (event: TimelineEvent, i: number) => {
		const prevGroup = groupedEvents[i - 1]
		const nextGroup = groupedEvents[i + 1]
		const prevEvent = prevGroup?.events[prevGroup.events.length - 1]
		const nextEvent = nextGroup?.events[0]

		items.push({
			type: "single-other",
			event,
			hasEventAbove:
				prevGroup?.type === "other" &&
				prevEvent !== undefined &&
				isSystemEvent(prevEvent) &&
				isSystemEvent(event),
			hasEventBelow:
				nextGroup?.type === "other" &&
				nextEvent !== undefined &&
				isSystemEvent(nextEvent) &&
				isSystemEvent(event),
		})
	}

	const flushRun = (nextIndex: number) => {
		if (currentRun.length === 0) return

		if (currentRun.length >= MIN_FOLDED_RUN_LENGTH) {
			// Check if there's a system event before the run
			const beforeRun =
				runStartIndex > 0 ? groupedEvents[runStartIndex - 1] : undefined
			const beforeEvent = beforeRun?.events[beforeRun.events.length - 1]
			const hasRunAbove =
				beforeRun?.type === "other" &&
				beforeEvent !== undefined &&
				isSystemEvent(beforeEvent)

			// Check if there's a system event after the run
			const afterRun =
				nextIndex < groupedEvents.length ? groupedEvents[nextIndex] : undefined
			const afterEvent = afterRun?.events[0]
			const hasRunBelow =
				afterRun?.type === "other" &&
				afterEvent !== undefined &&
				isSystemEvent(afterEvent)

			items.push({
				type: "run",
				events: [...currentRun],
				hasRunAbove,
				hasRunBelow,
			})
		} else {
			// Render individually as before
			currentRun.forEach((event, offset) =>
				pushSingleSystemEvent(event, runStartIndex + offset),
			)
		}
		currentRun = []
	}

	let resolvedRun: TimelineEvent[] = []
	const flushResolvedRun = () => {
		if (resolvedRun.length === 0) return
		items.push({ type: "resolved-comments", events: [...resolvedRun] })
		resolvedRun = []
	}

	groupedEvents.forEach((group, i) => {
		if (group.type === "comment" && isResolvedCommentEvent(group.events[0])) {
			flushRun(i)
			resolvedRun.push(group.events[0]!)
			return
		}
		flushResolvedRun()

		if (group.type === "other") {
			const event = group.events[0]
			if (event && isSystemEvent(event)) {
				// The customer's own verdict on the agent is what the team reads the
				// chat for, so a folded run never hides it.
				if (isAiChatFeedbackEvent(event)) {
					flushRun(i)
					pushSingleSystemEvent(event, i)
					return
				}
				if (currentRun.length === 0) runStartIndex = i
				currentRun.push(event)
				return
			}
			// Non-system "other" event — flush and render individually
			flushRun(i)
			items.push({
				type: "single-other",
				event: event!,
				hasEventAbove: false,
				hasEventBelow: false,
			})
			return
		}

		flushRun(i)
		items.push({
			type: "group",
			group,
			key: groupKey(group.events),
		})
	})

	flushRun(groupedEvents.length)
	flushResolvedRun()
	return items
}

// Main render function for grouped events
const renderGroupedEvents = (
	events: TimelineEvent[],
	onMessageUndo: (content: string) => void,
	deviceInfoForEventId?: string,
	deviceInfoText?: string,
	followsNote = false,
) => {
	const answeredAt = latestAnswerAt(events)
	const groupedEvents = groupEventsBySender(
		events.filter((event) => !isHiddenShadowReply(event, answeredAt)),
	)
	const renderItems = buildRenderItems(groupedEvents)

	let firstEmailId: string | undefined
	let firstEmailCreatedAt = Number.POSITIVE_INFINITY
	for (const event of events) {
		if (event.type === "email" && event.email.createdAt < firstEmailCreatedAt) {
			firstEmailCreatedAt = event.email.createdAt
			firstEmailId = event.email.id
		}
	}

	return renderItems.map((item, index) => {
		const isFirst = index === 0
		const isLast = index === renderItems.length - 1
		switch (item.type) {
			case "run": {
				// Key by the newest event: loading older history prepends
				// into the run, keying by the first event would remount it.
				const newestEvent = item.events[item.events.length - 1]
				const key = newestEvent
					? `run-${getEventStableId(newestEvent)}`
					: `run-empty`
				return (
					<div
						key={key}
						className={systemEventEdgeSpacing({
							hasAbove: item.hasRunAbove,
							hasBelow: item.hasRunBelow,
							isFirst,
							isLast,
							followsNote,
						})}
					>
						<CollapsibleActivityRun
							events={item.events}
							hasRunAbove={item.hasRunAbove}
							hasRunBelow={item.hasRunBelow}
							renderEvent={renderNonGroupableEvent}
						/>
					</div>
				)
			}
			case "single-other": {
				const key = `single-${getEventStableId(item.event)}`
				return (
					<div
						key={key}
						className={systemEventEdgeSpacing({
							hasAbove: item.hasEventAbove,
							hasBelow: item.hasEventBelow,
							isFirst,
							isLast,
							followsNote,
						})}
					>
						{renderNonGroupableEvent(
							item.event,
							item.hasEventAbove,
							item.hasEventBelow,
						)}
					</div>
				)
			}
			case "resolved-comments": {
				const newest = item.events[item.events.length - 1]
				return (
					<div key={`resolved-${newest ? getEventStableId(newest) : "empty"}`}>
						<ResolvedCommentsRun events={item.events} />
					</div>
				)
			}
			case "group": {
				return (
					<div key={item.key}>
						{renderGroupedMessages(
							item.group,
							onMessageUndo,
							firstEmailId,
							deviceInfoForEventId,
							deviceInfoText,
						)}
					</div>
				)
			}
		}
	})
}

const getEventTimestamp = (event: TimelineEvent): number => {
	switch (event.type) {
		case "createdAt":
			return event.thread.createdAt
		case "email":
			return event.email.createdAt
		case "comment":
			return event.comment.createdAt
		case "threadMessage":
			return event.threadMessage.createdAt
		case "slackMessage":
			return event.slackMessage.createdAt
		case "chatMessage":
			return event.chatMessage.createdAt
		case "live-chat-message":
			return event.liveChatMessage.createdAt
		case "live-chat-closed":
			return event.liveChat.finishedAt || Date.now()
		case "activity":
			return event.threadActivity.createdAt
		// Oldest member, so the gapless cut hides a burst until all of it is synced.
		case "agentActivityBurst":
			return event.burst.firstCreatedAt
		case "routineRun":
			return event.run.createdAt
	}
}

interface GatedEvents {
	events: TimelineEvent[]
	showNote: boolean
	hasOlder: boolean
}

// Syncing: walkback capped at feedbackMessagePreloadSize per type. Complete:
// cut above the gating boundary, keep settled snapshot during re-syncs.
const useGaplessEvents = (
	events: TimelineEvent[],
	isComplete: boolean,
	gating: TimelineGating,
): GatedEvents => {
	const cappedResult = useMemo(() => {
		if (!isComplete) return null
		const boundary = gating.boundary
		if (!boundary) {
			return { events, showNote: true, hasOlder: false }
		}
		return {
			// Strict cut: ties at the boundary timestamp stay behind the
			// button, a same-timestamp sibling may be unsynced.
			events: events.filter(
				(event) => getEventTimestamp(event) > boundary.createdAt,
			),
			showNote: false,
			hasOlder: true,
		}
	}, [isComplete, events, gating.boundary])

	const [settled, setSettled] = useState<GatedEvents | null>(null)
	useEffect(() => {
		if (cappedResult) setSettled(cappedResult)
	}, [cappedResult])

	const walkbackResult = useMemo<GatedEvents>(() => {
		const eventCounts = events.reduce(
			(acc: Record<TimelineEvent["type"], number>, event: TimelineEvent) => {
				acc[event.type] = feedbackMessagePreloadSize // dynamic version: (acc[event.type] || 0) + 1
				return acc
			},
			{} as Record<TimelineEvent["type"], number>,
		)

		const result: TimelineEvent[] = []
		let showNote = false
		for (let i = events.length - 1; i >= 0; i--) {
			const event = events[i]
			if (!event) break
			result.unshift(event)

			const eventCount = --eventCounts[event.type]
			if (event.type === "createdAt") {
				showNote = true
			}
			if (eventCount === 0) break
		}

		return { events: result, showNote, hasOlder: false }
	}, [events])

	return cappedResult ?? settled ?? walkbackResult
}

export const MainContent: FC<Props> = memo(
	({ editor, onMessageUndo, isEditorOpen, onHideNotes }) => {
		const viewport = useRef<HTMLDivElement>(null)
		const prevHeightRef = useRef<number>(0)

		// Maintain scroll position when viewport height changes (e.g., bottom bar resizes after sending)
		// With flex-col-reverse, scrollTop=0 is the bottom. When viewport grows, Chrome may not
		// maintain the visual position correctly, causing content to shift.
		const { thread } = useThreadContext()

		// Keyed by thread: a height carried over from the last thread would scroll
		// this one up by the difference and tuck its newest message under the composer.
		useEffect(() => {
			const el = viewport.current
			if (!el) return
			prevHeightRef.current = el.clientHeight
			const observer = new ResizeObserver(() => {
				const newHeight = el.clientHeight
				const next = scrollTopAfterResize({
					scrollTop: el.scrollTop,
					prevHeight: prevHeightRef.current,
					newHeight,
				})
				if (next !== null) el.scrollTop = next
				prevHeightRef.current = newHeight
			})
			observer.observe(el)
			return () => observer.disconnect()
		}, [thread.id])

		return (
			<div className="relative min-h-0 flex-1">
				<ScrollArea>
					{/* contain: at either end a touch scroll would otherwise chain to the
					    page and bounce the whole app on iOS */}
					<ScrollViewport
						id="messages"
						className="flex flex-col-reverse overscroll-y-contain"
						ref={viewport}
					>
						<div className={TIMELINE_BOTTOM_FADE_CLASS} />
						<MainContentBody
							key={thread.id}
							editor={editor}
							onMessageUndo={onMessageUndo}
							isEditorOpen={isEditorOpen}
							onHideNotes={onHideNotes}
						/>
					</ScrollViewport>
				</ScrollArea>
			</div>
		)
	},
)

const MainContentBody: FC<Props> = ({
	editor,
	onMessageUndo,
	isEditorOpen,
	onHideNotes,
}) => {
	const {
		thread,
		queryResult,
		baseLimit,
		pages,
		loadOlderMessages,
		growBaseLimit,
	} = useThreadContext()
	const contact = thread?.contact

	const { pageData, pagesNode } = useTimelinePages(thread.id, pages)
	const gating = useMemo(
		() => computeTimelineGating(thread, baseLimit, pageData),
		[thread, baseLimit, pageData],
	)
	const timelineSyncing =
		queryResult.type !== "complete" || pageData.some((p) => !p.complete)
	// Keyed by thread id, so this resets on every switch.
	const [hasSynced, setHasSynced] = useState(!timelineSyncing)
	if (!timelineSyncing && !hasSynced) setHasSynced(true)

	// New messages can slide the base window above page 1's cursor; grow the
	// base until it overlaps the pages again so history stays gapless.
	useEffect(() => {
		if (gating.needsBaseHeal && queryResult.type === "complete") {
			growBaseLimit()
		}
	}, [gating.needsBaseHeal, queryResult.type, growBaseLimit])

	// Events
	const allEvents = useTimelineEvents(gating.mergedThread)
	const slackGrouping = useMemo(() => {
		if (thread.origin !== "SLACK_CONNECT" || !thread.slackChannelId) return null
		const slackMessages = allEvents.flatMap((event) =>
			event.type === "slackMessage" ? [event.slackMessage] : [],
		)
		const joined = joinedSlackMessages(slackMessages, thread.slackReplyId)
		if (joined.length === 0) return null
		return {
			threadId: thread.id,
			slackChannelId: thread.slackChannelId,
			joined: joined.map((message) => ({
				id: message.id,
				createdAt: message.createdAt,
				authorName: message.fromContactName || "Slack user",
				text: message.html || message.text,
			})),
		}
	}, [
		allEvents,
		thread.id,
		thread.origin,
		thread.slackChannelId,
		thread.slackReplyId,
	])
	const renderedMessageIds = useMemo(() => messageIds(allEvents), [allEvents])
	const showSkeleton = shouldShowTimelineSkeleton(allEvents, {
		syncing: timelineSyncing,
		hasSynced,
	})
	const gaplessEvents = useGaplessEvents(
		allEvents,
		!timelineSyncing && !gating.needsBaseHeal,
		gating,
	)

	const olderSentinelRef = useRef<HTMLDivElement>(null)
	const [isOlderSentinelVisible, setIsOlderSentinelVisible] = useState(false)

	useEffect(() => {
		const sentinel = olderSentinelRef.current
		if (!sentinel) {
			setIsOlderSentinelVisible(false)
			return
		}
		const observer = new IntersectionObserver(
			([entry]) => setIsOlderSentinelVisible(entry?.isIntersecting ?? false),
			// rootMargin prefetches the next page before the top edge is reached.
			{ root: document.getElementById("messages"), rootMargin: "300px 0px" },
		)
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [gaplessEvents.hasOlder])

	// While the sentinel stays in view, each settled page recomputes nextCursor
	// and this refires, chain-loading until history ends or the user scrolls away.
	useEffect(() => {
		if (!isOlderSentinelVisible) return
		if (timelineSyncing || gating.needsBaseHeal) return
		if (gating.nextCursor) loadOlderMessages(gating.nextCursor)
	}, [
		isOlderSentinelVisible,
		timelineSyncing,
		gating.needsBaseHeal,
		gating.nextCursor,
		loadOlderMessages,
	])

	const shouldShowNote =
		!showSkeleton &&
		gaplessEvents.showNote &&
		editor &&
		(gaplessEvents.events.length === 0 || !editor.isEmpty || isEditorOpen) &&
		(isEditorOpen || !isThreadNoteEditable(thread))

	const deviceInfoPlacement = useMemo(
		() =>
			resolveDeviceInfoPlacement(gaplessEvents.events, {
				canAttachToThreadNote:
					!!shouldShowNote && !isThreadNoteEditable(thread),
			}),
		[gaplessEvents.events, shouldShowNote, thread],
	)

	const eventsForRender = useMemo(() => {
		if (!deviceInfoPlacement) return gaplessEvents.events
		return gaplessEvents.events.filter(
			(event) => !isDeviceInfoCommentEvent(event),
		)
	}, [gaplessEvents.events, deviceInfoPlacement])

	const deviceInfoForEventId =
		deviceInfoPlacement && "targetEventId" in deviceInfoPlacement
			? deviceInfoPlacement.targetEventId
			: undefined
	const deviceInfoText = deviceInfoPlacement?.text
	const deviceInfoForThreadNote =
		deviceInfoPlacement &&
		"target" in deviceInfoPlacement &&
		deviceInfoPlacement.target === "thread-note"
			? deviceInfoPlacement.text
			: undefined

	const { hoveredIds, prevHoveredIds } = useHighlights()
	useMarkHighlights({ hoveredIds, prevHoveredIds })

	// only for live chat
	const {
		names: typingNames,
		handleTyping,
		clear: clearTyping,
	} = useTypingUsers()

	// Teammates replying on this thread come through doc presence and lead the
	// row; their copy on the chat socket is dropped so each person shows once.
	const teamTyping = useDocTyping(`thread:${thread.id}`)
	const typingTeammate = teamTyping[0]
	const allTypingNames = useMemo(
		() => [...new Set([...teamTyping.map((v) => v.name), ...typingNames])],
		[typingNames, teamTyping],
	)
	const memberEmails = useMemberEmails()

	const { liveChatSocket, isLiveChatConnected } = useLiveChatStore()
	const liveChatSessionId = thread.liveChatSession?.id

	useEffect(() => {
		if (!isLiveChatConnected || !liveChatSocket || !liveChatSessionId) {
			clearTyping()
			return
		}

		const onUserTyping = (data: LiveChatUserTypingData) => {
			if (
				data.isTeammate ||
				memberEmails.current.has((data.email ?? "").toLowerCase())
			)
				return
			if (isTypingForSession(data, liveChatSessionId)) handleTyping(data)
		}

		liveChatSocket.on("live-chat:user-typing", onUserTyping)
		liveChatSocket.on("live-chat:user-left", clearTyping)

		return () => {
			liveChatSocket.off("live-chat:user-typing", onUserTyping)
			liveChatSocket.off("live-chat:user-left", clearTyping)
			clearTyping()
		}
	}, [
		liveChatSocket,
		isLiveChatConnected,
		liveChatSessionId,
		memberEmails,
		handleTyping,
		clearTyping,
	])

	const attachments = useMemo<FormattedAttachment[]>(() => {
		return (thread.attachments ?? []).map((attachment) => {
			const parsed =
				typeof attachment === "string"
					? (JSON.parse(attachment) as FormattedAttachment)
					: (attachment as unknown as FormattedAttachment)
			return {
				...parsed,
				Content: parsed.Content ?? "",
			}
		})
	}, [thread])

	const contactImageUrl = useMemo(() => {
		if (contact?.imageUrl) return contact.imageUrl
		const firstInboundSlackMessage = allEvents.find(
			(e) => e.type === "slackMessage" && e.slackMessage.type === "INBOUND",
		)
		if (
			firstInboundSlackMessage?.type === "slackMessage" &&
			firstInboundSlackMessage.slackMessage.fromContactImage
		) {
			return firstInboundSlackMessage.slackMessage.fromContactImage
		}
		return undefined
	}, [contact?.imageUrl, allEvents])

	return (
		<div
			className={cn(
				"z-20 mx-auto flex max-w-[50rem] flex-col gap-0 sm:pt-4",
				THREAD_COLUMN_PADDING,
				TIMELINE_BOTTOM_PADDING_CLASS,
			)}
		>
			{/* Internal note */}
			{shouldShowNote && (
				<ThreadContentEvent
					editor={editor}
					senderName={contact ? getContactDisplayName(contact) : undefined}
					senderEmail={contact?.email}
					senderImage={contactImageUrl}
					timestamp={new Date(thread.createdAt)}
					attachments={attachments}
					hasText={!!thread.text}
					origin={thread.origin}
					onHideNotes={onHideNotes}
					companyName={thread.company?.name}
					companyLogoUrl={thread.company?.logoUrl}
					senderIsUnverified={thread.contactIsUnverified ?? false}
					deviceInfoText={deviceInfoForThreadNote}
					hasAiChatTranscript={(thread.threadMessages?.length ?? 0) > 0}
				/>
			)}

			{pagesNode}

			{gaplessEvents.hasOlder && (
				// overflow-anchor none: without it Chrome scroll-anchors on the
				// sentinel and the view jumps to the top of each loaded page.
				<div
					ref={olderSentinelRef}
					className="flex min-h-8 items-center justify-center pb-4 pt-1 [overflow-anchor:none]"
				>
					{(timelineSyncing || gating.needsBaseHeal) && <Loader />}
				</div>
			)}

			{/* Events (E-Mail, Comment, Thread Message, ...) */}
			<SlackGroupingContext.Provider value={slackGrouping}>
				<div className="w-full">
					{showSkeleton ? (
						<TimelineSkeleton />
					) : (
						renderGroupedEvents(
							eventsForRender,
							onMessageUndo,
							deviceInfoForEventId,
							deviceInfoText,
							!!shouldShowNote,
						)
					)}
				</div>
			</SlackGroupingContext.Provider>

			<UnsentMessages threadId={thread.id} renderedIds={renderedMessageIds} />

			{/* Zero-height anchor: the row floats over the padding below the last
			    message, so appearing and disappearing moves nothing */}
			<div className={TYPING_ANCHOR_CLASS}>
				{allTypingNames.length > 0 && (
					<div className={TYPING_ROW_CLASS}>
						<TypingProgress
							className={TYPING_LABEL_GAP_CLASS}
							names={allTypingNames}
							avatar={
								<div className={TYPING_AVATAR_COLUMN_CLASS}>
									{typingTeammate ? (
										<Avatar
											name={typingTeammate.name}
											image={typingTeammate.imageUrl ?? undefined}
											size="xxs"
											roundness="full"
										/>
									) : (
										<Avatar
											name={
												contact?.name ??
												(contact?.email
													? generateNameFromEmail(contact.email)
													: undefined) ??
												typingNames[0] ??
												""
											}
											colorSeed={contact?.email}
											image={contactImageUrl}
											showAvatarFallback={
												!contactImageUrl && !contact?.name && !contact?.email
											}
											size="xxs"
											roundness="full"
										/>
									)}
								</div>
							}
						/>
					</div>
				)}
			</div>
		</div>
	)
}
